import {
    VersionedTransaction,
    PublicKey,
    Connection,
    AddressLookupTableAccount,
    SimulatedTransactionResponse,
    RpcResponseAndContext
} from "@solana/web3.js";
import { AccountLayout, TOKEN_PROGRAM_ID } from "@solana/spl-token";

import { RPC_URL, WRAPPED_SOL_MINT } from "../../constants";
import { base64 } from "../bytes";
import { logTransactionDebugInfo } from "./debug";
import { Result, SendableTransaction } from "../../types";
import { sendTransactionAnalytics } from "./analytics";
import { AnalyticsInfo, BalanceChanges } from "./types";
import { TransactionStatus } from "../../types/transaction";

// TOKEN_2022_PROGRAM_ID isn't reachable via classic moduleResolution here because
// @solana/spl-token 0.4's .d.ts re-exports use .js extensions and a nested 0.1.8
// gets picked up first. Mirrors the same workaround in rpcApi.ts.
const TOKEN_2022_PROGRAM_ID = new PublicKey("TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb");

function getSimulationFailureLogs(errorMessage: string, logs?: string[] | null) {
    return logs?.length ? [errorMessage, ...logs] : [errorMessage];
}

export async function simulateTransactionAndGetBalanceChanges<T extends AnalyticsInfo = AnalyticsInfo>({
    transaction,
    user,
    accountsToTrack,
    analyticsInfo
}: {
    transaction: VersionedTransaction;
    user: PublicKey;
    accountsToTrack: PublicKey[];
    analyticsInfo?: T;
}): Promise<{ balanceChanges: BalanceChanges; simulationLogs?: string[] }> {
    const connection = new Connection(RPC_URL);
    const sanitizedTx = sanitizeForSimulation(transaction);
    let simResult: RpcResponseAndContext<SimulatedTransactionResponse> | undefined;

    try {
        simResult = await connection.simulateTransaction(sanitizedTx, {
            sigVerify: false,
            replaceRecentBlockhash: true,
            accounts: {
                encoding: "base64",
                addresses: accountsToTrack.map((a) => a.toBase58())
            }
        });
    } catch (error) {
        await logTransactionDebugInfo(transaction, connection);
        const errorMessage = error instanceof Error ? error.message : String(error);
        sendTransactionAnalytics(user, {
            ...analyticsInfo,
            transactionStatus: TransactionStatus.Error,
            simulation: true,
            errorMessage
        });
        Result.err(`Transaction simulation failed: ${errorMessage}`);
        return { balanceChanges: {}, simulationLogs: [errorMessage] };
    }

    if (simResult?.value?.err) {
        await logTransactionDebugInfo(transaction, connection, simResult?.value?.logs ?? undefined);
        const errorMessage = JSON.stringify(simResult.value.err);
        sendTransactionAnalytics(user, {
            ...analyticsInfo,
            transactionStatus: TransactionStatus.Error,
            simulation: true,
            errorMessage
        });
        Result.err(`Transaction simulation failed: ${errorMessage}`);
        return {
            balanceChanges: {},
            simulationLogs: getSimulationFailureLogs(errorMessage, simResult.value.logs)
        };
    }

    const postMap: Record<string, string> = {};
    const taToMintMap: Record<string, string> = {};

    if (simResult?.value?.accounts) {
        for (let i = 0; i < simResult.value.accounts.length; i++) {
            const accountOpt = simResult.value.accounts[i];
            const pkStr = accountsToTrack[i].toBase58();

            if (pkStr === user.toBase58()) {
                postMap[pkStr] = accountOpt ? accountOpt.lamports.toString() : "0";
                continue;
            }

            if (accountOpt) {
                // Try SPL first: only handle if accountOpt.data is array (i.e., base64 format)
                if (Array.isArray(accountOpt.data) && accountOpt.data.length > 0) {
                    const dataB64: string = accountOpt.data[0];
                    try {
                        const decoded = base64.decode(dataB64);

                        try {
                            const tokenAcc = AccountLayout.decode(decoded);

                            const owner = tokenAcc.owner;
                            if (owner.equals(user)) {
                                const mint = tokenAcc.mint.toBase58();
                                const amount = tokenAcc.amount.toString();

                                postMap[pkStr] = amount;
                                taToMintMap[pkStr] = mint;
                            }
                        } catch (decodeErr) {
                            // Equivalent to Rust’s “if let Ok(token_acc)” — ignore invalid SPL accounts
                            // (not a token account)
                            continue;
                        }
                    } catch (b64Err) {
                        Result.err(`Failed to decode base64 for ${pkStr}`);
                    }
                }
                if (accountsToTrack[i].equals(user)) {
                    postMap[pkStr] = accountOpt.lamports.toString();
                }
            } else {
                continue;
            }
        }
    }

    // Pubkey -> current numeric balance (SPL amount or lamports)
    const currentMap: Record<string, string> = {};
    const currentInfos = await connection.getMultipleAccountsInfo(accountsToTrack);

    for (let idx = 0; idx < currentInfos.length; idx++) {
        const pk = accountsToTrack[idx].toBase58();
        const uiAcc = currentInfos[idx];
        if (uiAcc) {
            // Try SPL token decode first, pattern like above
            try {
                // AccountLayout.decode blindly parses any 165-byte buffer; without an
                // owner-program guard, non-token accounts of that size would produce
                // garbage that bypasses the catch-fallback to lamports.
                if (!uiAcc.owner.equals(TOKEN_PROGRAM_ID) && !uiAcc.owner.equals(TOKEN_2022_PROGRAM_ID)) {
                    throw new Error("Not a token account");
                }
                const tokenAcc = AccountLayout.decode(uiAcc.data);
                const mint = tokenAcc.mint.toBase58();
                const amount = tokenAcc.amount.toString();

                currentMap[pk] = amount;
                taToMintMap[pk] = mint;
            } catch (error) {
                // If not SPL decodeable, treat as lamports (system account/SOL/PDAs)
                currentMap[pk] = uiAcc.lamports.toString();
            }
        } else {
            currentMap[pk] = "0";
        }
    }

    const balanceChanges: Record<string, number> = {};
    for (const [pk, postValStr] of Object.entries(postMap)) {
        const currentValStr = currentMap[pk] ?? "0";
        const postVal = BigInt(postValStr);
        const currentVal = BigInt(currentValStr);
        const delta = postVal - currentVal;
        const mint = taToMintMap[pk] ?? WRAPPED_SOL_MINT;
        if (delta !== BigInt(0)) {
            //Note: Will loose precision since we cast as number. Needs to be converted to u64 class.
            balanceChanges[mint] = (balanceChanges[mint] ?? 0) + Number(delta);
        }
    }

    // need to merge balance changes of same mint
    const mergedBalanceChanges: Record<string, number> = {};
    for (const [mint, change] of Object.entries(balanceChanges)) {
        mergedBalanceChanges[mint] = (mergedBalanceChanges[mint] ?? 0) + change;
    }

    return { balanceChanges: mergedBalanceChanges, simulationLogs: undefined };
}

export async function simulateTransactionAndSendAlert<T extends AnalyticsInfo = AnalyticsInfo>({
    transaction,
    user,
    analyticsInfo
}: {
    transaction: SendableTransaction;
    user: PublicKey | string;
    analyticsInfo?: T;
}): Promise<void> {
    const connection = new Connection(RPC_URL);
    let simResult: RpcResponseAndContext<SimulatedTransactionResponse> | undefined;

    try {
        if (transaction instanceof VersionedTransaction) {
            const sanitizedTx = sanitizeForSimulation(transaction);
            simResult = await connection.simulateTransaction(sanitizedTx, {
                sigVerify: false,
                replaceRecentBlockhash: true
            });
        } else {
            simResult = await connection.simulateTransaction(transaction);
        }
    } catch (error) {
        if (transaction instanceof VersionedTransaction) {
            await logTransactionDebugInfo(transaction, connection);
        }
        const errorMessage = error instanceof Error ? error.message : String(error);
        sendTransactionAnalytics(user, {
            ...analyticsInfo,
            transactionStatus: TransactionStatus.Error,
            simulation: true,
            errorMessage
        });
        Result.err(`Transaction simulation failed: ${errorMessage}`);
        return;
    }

    if (simResult?.value?.err) {
        if (transaction instanceof VersionedTransaction) {
            await logTransactionDebugInfo(transaction, connection);
        }
        const errorMessage = JSON.stringify(simResult.value.err);
        sendTransactionAnalytics(user, {
            ...analyticsInfo,
            transactionStatus: TransactionStatus.Error,
            simulation: true,
            errorMessage
        });
        Result.err(`Transaction simulation failed: ${errorMessage}`);
    }
}

/**
 * Sanitize the transaction for simulation by ensuring the signatures array
 * has the correct length of dummy signatures without relying on serialization,
 * which may fail if the signature array is missing or mis-sized.
 *
 * RangeError: encoding overruns Uint8Array is thrown if you try to serialize
 * transactions that have fewer signatures than required.
 */
function sanitizeForSimulation(tx: VersionedTransaction): VersionedTransaction {
    // shallow clone as VersionedTransaction
    const sanitized = new VersionedTransaction(tx.message);

    // Number of required signatures, as per Solana message header
    const numRequiredSignatures = tx.message.header.numRequiredSignatures;

    sanitized.signatures = Array(numRequiredSignatures)
        .fill(null)
        .map(() => new Uint8Array(64));

    return sanitized;
}

export async function resolveAddressLookups(transaction: VersionedTransaction): Promise<AddressLookupTableAccount[]> {
    const connection = new Connection(RPC_URL);
    const lookups = transaction.message.addressTableLookups;

    const lutAccounts: AddressLookupTableAccount[] = [];

    for (const lookup of lookups) {
        const accInfo = await connection.getAccountInfo(lookup.accountKey);
        if (!accInfo) continue;

        const lut = AddressLookupTableAccount.deserialize(new Uint8Array(accInfo.data.buffer));
        lutAccounts.push(new AddressLookupTableAccount({ key: lookup.accountKey, state: lut }));
    }

    return lutAccounts;
}

type SimulateBundleResponse = {
    error?: { message?: string };
    result?: {
        value?: {
            summary?: unknown;
            transactionResults?: BundleTransactionSimulationResult[];
        };
    };
};

type BundleSimulatedAccount = {
    data?: [string, string] | string;
    lamports: number;
    owner?: string;
} | null;

type BundleTransactionSimulationResult = {
    preExecutionAccounts?: BundleSimulatedAccount[];
    postExecutionAccounts?: BundleSimulatedAccount[];
};

export type SimulateBundleAccountsConfig = {
    accountsToTrack: string[];
    userAddress: string;
};

export type SimulateBundleResult = {
    balanceChanges: BalanceChanges;
};

export async function simulateBundle(
    encodedTransactions: string[],
    accountsConfig?: SimulateBundleAccountsConfig
): Promise<Result<SimulateBundleResult>> {
    try {
        const preExecutionAccountsConfigs = Array(encodedTransactions.length).fill(null);
        const postExecutionAccountsConfigs = Array(encodedTransactions.length).fill(null);

        if (accountsConfig?.accountsToTrack.length && encodedTransactions.length) {
            const config = {
                addresses: accountsConfig.accountsToTrack,
                encoding: "base64"
            };
            preExecutionAccountsConfigs[0] = config;
            postExecutionAccountsConfigs[encodedTransactions.length - 1] = config;
        }

        const response = await fetch(RPC_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                jsonrpc: "2.0",
                id: "simulate-bundle",
                method: "simulateBundle",
                params: [
                    { encodedTransactions },
                    {
                        preExecutionAccountsConfigs,
                        postExecutionAccountsConfigs,
                        skipSigVerify: true
                    }
                ]
            })
        });

        const body = (await response.json()) as SimulateBundleResponse;
        const errorMessage = extractBundleSimulationError(body, response.ok);
        if (errorMessage) {
            // eslint-disable-next-line no-console
            console.error("[sim] simulateBundle error response", {
                ok: response.ok,
                status: response.status,
                statusText: response.statusText,
                body
            });
            return Result.errFromMessage(errorMessage, { skipSentry: true, rawError: body });
        }

        const balanceChanges = accountsConfig
            ? getBundleBalanceChanges(body, accountsConfig.accountsToTrack, accountsConfig.userAddress)
            : {};

        return Result.ok({ balanceChanges });
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return Result.errFromMessage(message, { skipSentry: true, rawError: error });
    }
}

function extractBundleSimulationError(res: SimulateBundleResponse, responseOk: boolean): string | undefined {
    if (!responseOk) {
        return "Bundle simulation request failed";
    }

    if (res.error?.message) return res.error.message;

    const simulationValue = res.result?.value;
    if (!simulationValue) {
        return "Bundle simulation returned no result";
    }

    if (simulationValue.summary && simulationValue.summary !== "succeeded") {
        return `Bundle simulation ${formatBundleSimulationSummary(simulationValue.summary)}`;
    }

    return undefined;
}

function getBundleBalanceChanges(
    response: SimulateBundleResponse,
    accountsToTrack: string[],
    userAddress: string
): BalanceChanges {
    const transactionResults = response.result?.value?.transactionResults;
    if (!transactionResults?.length) return {};

    const preAccounts = transactionResults[0]?.preExecutionAccounts;
    const postAccounts = transactionResults[transactionResults.length - 1]?.postExecutionAccounts;
    if (!preAccounts || !postAccounts) return {};

    const balanceChanges: BalanceChanges = {};

    accountsToTrack.forEach((address, index) => {
        const pre = parseBundleAccountBalance(preAccounts[index], address, userAddress);
        const post = parseBundleAccountBalance(postAccounts[index], address, userAddress);
        const mint = post?.mint ?? pre?.mint;
        if (!mint) return;

        const delta = (post?.amount ?? BigInt(0)) - (pre?.amount ?? BigInt(0));
        if (delta === BigInt(0)) return;

        balanceChanges[mint] = (balanceChanges[mint] ?? 0) + Number(delta);
    });

    return balanceChanges;
}

function parseBundleAccountBalance(
    account: BundleSimulatedAccount | undefined,
    address: string,
    userAddress: string
): { mint: string; amount: bigint } | undefined {
    if (!account) return undefined;

    if (address === userAddress) {
        return { mint: WRAPPED_SOL_MINT, amount: BigInt(account.lamports) };
    }

    if (account.owner !== TOKEN_PROGRAM_ID.toBase58() && account.owner !== TOKEN_2022_PROGRAM_ID.toBase58()) {
        return undefined;
    }

    const encodedData = Array.isArray(account.data) ? account.data[0] : account.data;
    if (!encodedData) return undefined;

    try {
        const tokenAccount = AccountLayout.decode(base64.decode(encodedData));
        if (!tokenAccount.owner.equals(new PublicKey(userAddress))) return undefined;

        return {
            mint: tokenAccount.mint.toBase58(),
            amount: BigInt(tokenAccount.amount.toString())
        };
    } catch {
        return undefined;
    }
}

function formatBundleSimulationSummary(summary: unknown): string {
    if (typeof summary === "string") return summary;

    try {
        return JSON.stringify(summary);
    } catch {
        return String(summary);
    }
}
